import { useState } from 'react'
import { Box, Chip, Tooltip, Popover, IconButton } from '@mui/material'
import { AddReaction } from '@mui/icons-material'
import { apiClient } from '@/services/apiClient'
import type { ReactionGroup } from '@/types'

const EMOJI_OPTIONS = ['❤️', '🙏', '👍', '😊', '😢', '🎉']

interface EmojiReactionsProps {
  entityId: string
  entityType: 'prayerrequest' | 'prayerrequestcomment' | 'discussion' | 'discussioncomment'
  reactions?: ReactionGroup[] | null
  onReactionsChange: (reactions: ReactionGroup[]) => void
}

export default function EmojiReactions({ entityId, entityType, reactions: rawReactions, onReactionsChange }: EmojiReactionsProps) {
  const reactions = rawReactions ?? []
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleToggle = async (emoji: string) => {
    setAnchorEl(null)
    if (submitting) return
    setSubmitting(true)
    try {
      const updated = await apiClient.post<ReactionGroup[]>(`/api/reactions/${entityType}/${entityId}`, { emoji })
      onReactionsChange(updated)
    } catch {
      // Silently ignore — reaction is best-effort
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
      {reactions.map(group => (
        <Tooltip
          key={group.emoji}
          title={group.userDisplayNames.join(', ')}
          arrow
        >
          <Chip
            label={`${group.emoji} ${group.count}`}
            size="small"
            variant={group.currentUserReacted ? 'filled' : 'outlined'}
            color={group.currentUserReacted ? 'primary' : 'default'}
            onClick={() => handleToggle(group.emoji)}
            disabled={submitting}
            sx={{
              cursor: 'pointer',
              fontSize: '0.8rem',
              height: 26,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        </Tooltip>
      ))}
      <Tooltip title="Add reaction" arrow>
        <IconButton
          size="small"
          onClick={e => setAnchorEl(e.currentTarget)}
          disabled={submitting}
          sx={{ width: 26, height: 26 }}
        >
          <AddReaction sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 0.5, display: 'flex', gap: 0.25 } } }}
      >
        {EMOJI_OPTIONS.map(emoji => (
          <IconButton
            key={emoji}
            size="small"
            onClick={() => handleToggle(emoji)}
            sx={{ fontSize: '1.25rem', width: 36, height: 36 }}
          >
            {emoji}
          </IconButton>
        ))}
      </Popover>
    </Box>
  )
}
